// src/components/Merchants/MerchantsPage.js
// F7 — Merchant-level analytics dashboard
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  Stack,
  Grid,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StoreIcon from '@mui/icons-material/Store';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import api from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import { useInsightsFilter } from '../Insights/shared/InsightsFilterContext';
import InsightsDateFilter from '../Insights/shared/InsightsDateFilter';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const CHART_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#3b82f6', '#ef4444', '#14b8a6', '#f97316', '#84cc16',
];

// ─── Sub-component: KPI Card ──────────────────────────────────────────────────

function KpiCard({ label, value, icon, color, subtitle }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: '14px !important' }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: `${color}18`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color, flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
          <Typography variant="h6" fontWeight={700} noWrap>{value}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Sub-component: MerchantTransactions ─────────────────────────────────────

function MerchantTransactions({ merchantName, merchantKey, from, to }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [page, setPage]       = useState(1);

  const lookupName = merchantKey || merchantName;

  const load = useCallback(async (pg = 1) => {
    if (!lookupName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/analytics/merchants/${encodeURIComponent(lookupName)}/transactions`,
        { params: { from, to, page: pg, limit: 50 } }
      );
      setData(res.data.data);
      setPage(pg);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [lookupName, from, to]);

  useEffect(() => { load(1); }, [load]);

  if (loading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
  if (!data)   return null;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {data.total} transaction{data.total !== 1 ? 's' : ''} found
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.transactions.map((tx) => (
              <TableRow key={tx._id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.date)}</TableCell>
                <TableCell>{tx.description}</TableCell>
                <TableCell>
                  {tx.categoryId ? (
                    <Chip
                      label={tx.categoryId.name}
                      size="small"
                      sx={{
                        bgcolor: tx.categoryId.color ? `${tx.categoryId.color}22` : undefined,
                        color:   tx.categoryId.color || undefined,
                        fontSize: '0.7rem',
                      }}
                    />
                  ) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(Math.abs(tx.amount))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data.pages > 1 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
          <Button size="small" disabled={page <= 1} onClick={() => load(page - 1)}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page} / {data.pages}</Typography>
          <Button size="small" disabled={page >= data.pages} onClick={() => load(page + 1)}>Next</Button>
        </Stack>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MerchantsPage() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd     = useMediaQuery(theme.breakpoints.down('md'));

  const { from, to }              = useInsightsFilter();
  const [merchants, setMerchants] = useState([]);
  const [overview, setOverview]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [expanded, setExpanded]   = useState(null);
  const [sortField, setSortField] = useState('totalSpent');
  const [sortDir, setSortDir]     = useState('desc');
  const [search, setSearch]       = useState('');

  // ─ Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mRes, oRes] = await Promise.all([
        api.get('/analytics/merchants', { params: { from, to, limit: 50 } }),
        api.get('/analytics/overview',  { params: { from, to } }),
      ]);
      setMerchants(mRes.data.data || []);
      setOverview(oRes.data.data  || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─ Derived stats ──────────────────────────────────────────────────────────
  const totalSpend     = useMemo(() => merchants.reduce((s, m) => s + (m.totalSpent || 0), 0), [merchants]);
  const totalTx        = useMemo(() => merchants.reduce((s, m) => s + (m.txCount || 0),    0), [merchants]);
  const avgTx          = totalTx > 0 ? totalSpend / totalTx : 0;
  const biggestSingle  = overview?.biggestTransaction?.absAmount || 0;

  // ─ Sorting & filtering ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return merchants;
    const q = search.toLowerCase();
    return merchants.filter(m => (m.merchant || '').toLowerCase().includes(q));
  }, [merchants, search]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortField === 'merchant')  return dir * (a.merchant || '').localeCompare(b.merchant || '');
      if (sortField === 'txCount')   return dir * (a.txCount - b.txCount);
      if (sortField === 'avgAmount') return dir * ((a.avgAmount || 0) - (b.avgAmount || 0));
      if (sortField === 'maxAmount') return dir * ((a.maxAmount || 0) - (b.maxAmount || 0));
      if (sortField === 'lastSeen')  return dir * (new Date(a.lastSeen) - new Date(b.lastSeen));
      return dir * ((a.totalSpent || 0) - (b.totalSpent || 0));
    });
  }, [filtered, sortField, sortDir]);

  const handleSort = (field) => {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // ─ Chart data ─────────────────────────────────────────────────────────────
  const topChartData = useMemo(
    () => merchants.slice(0, 10).map((m, i) => {
      const name = m.merchant || 'Unknown';
      return {
        name:       name.length > 18 ? name.slice(0, 16) + '…' : name,
        fullName:   name,
        totalSpent: m.totalSpent || 0,
        fill:       CHART_COLORS[i % CHART_COLORS.length],
      };
    }),
    [merchants]
  );

  const monthlyData = overview?.monthlyTrend || [];
  const dowData     = overview?.dayOfWeek    || [];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>

      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <StoreIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>Merchant Analytics</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchAll} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* ── Date range (global Insights filter) ── */}
      <Box sx={{ mb: 3 }}>
        <InsightsDateFilter />
      </Box>

      {/* ── Error ── */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Loading ── */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }} color="text.secondary">Loading analytics…</Typography>
        </Box>
      )}

      {!loading && (
        <>
          {/* ── Section 1: KPI Cards ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                label: 'Total Spend',
                value: formatCurrency(totalSpend),
                icon: <TrendingUpIcon />,
                color: '#ef4444',
              },
              {
                label: 'Unique Merchants',
                value: merchants.length,
                icon: <StoreIcon />,
                color: '#6366f1',
              },
              {
                label: 'Total Transactions',
                value: totalTx,
                icon: <ReceiptLongIcon />,
                color: '#f59e0b',
              },
              {
                label: 'Avg Transaction',
                value: formatCurrency(avgTx),
                icon: <AttachMoneyIcon />,
                color: '#10b981',
                subtitle: `${totalTx} tx across ${merchants.length} merchants`,
              },
              {
                label: 'Biggest Single Tx',
                value: formatCurrency(biggestSingle),
                icon: <AttachMoneyIcon />,
                color: '#8b5cf6',
                subtitle: overview?.biggestTransaction
                  ? (overview.biggestTransaction.merchant || overview.biggestTransaction.description || '')
                  : '',
              },
            ].map(stat => (
              <Grid item xs={12} sm={6} md={4} lg={12 / 5} key={stat.label}>
                <KpiCard {...stat} />
              </Grid>
            ))}
          </Grid>

          {merchants.length === 0 && !error && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No transactions found for the selected date range.
            </Alert>
          )}

          {merchants.length > 0 && (
            <>
              {/* ── Section 2: Monthly Trend ── */}
              {monthlyData.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Monthly Spend Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={monthlyData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                        <defs>
                          <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} width={80} />
                        <RechartsTooltip
                          formatter={v => [formatCurrency(v), 'Total Spend']}
                          contentStyle={{
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="totalSpent"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#spendGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* ── Section 3: Top Merchants chart + ranked list ── */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Bar chart */}
                <Grid item xs={12} md={7}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                        Top {topChartData.length} Merchants by Spend
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart
                          data={topChartData}
                          margin={{ top: 4, right: 8, left: 8, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis
                            dataKey="name" tick={{ fontSize: 10 }}
                            angle={-35} textAnchor="end" interval={0}
                          />
                          <YAxis
                            tickFormatter={v => formatCurrency(v)}
                            tick={{ fontSize: 10 }} width={80}
                          />
                          <RechartsTooltip
                            formatter={(value, _name, props) => [
                              formatCurrency(value),
                              props.payload?.fullName || 'Spend',
                            ]}
                            contentStyle={{
                              background: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="totalSpent" radius={[4, 4, 0, 0]}>
                            {topChartData.map((entry, i) => (
                              <Cell key={i} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Ranked list */}
                <Grid item xs={12} md={5}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                        Rankings
                      </Typography>
                      <Stack spacing={1}>
                        {merchants.slice(0, 8).map((m, i) => {
                          const pct = totalSpend > 0 ? (m.totalSpent / totalSpend) * 100 : 0;
                          const color = CHART_COLORS[i % CHART_COLORS.length];
                          return (
                            <Box key={m.merchantKey || i}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ width: 18, color: 'text.secondary', flexShrink: 0, textAlign: 'right' }}
                                >
                                  {i + 1}
                                </Typography>
                                <Box
                                  sx={{
                                    width: 26, height: 26, borderRadius: '50%',
                                    bgcolor: `${color}22`, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    color, fontWeight: 700, fontSize: 12, flexShrink: 0,
                                  }}
                                >
                                  {(m.merchant || '?').charAt(0).toUpperCase()}
                                </Box>
                                <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 500 }}>
                                  {m.merchant || 'Unknown'}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                                  {formatCurrency(m.totalSpent)}
                                </Typography>
                              </Stack>
                              <Box sx={{ pl: '44px' }}>
                                <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                  <Box
                                    sx={{
                                      height: '100%', borderRadius: 2,
                                      bgcolor: color,
                                      width: `${Math.min(pct, 100)}%`,
                                      transition: 'width 0.6s ease',
                                    }}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {pct.toFixed(1)}% of total • {m.txCount} tx
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* ── Section 4: Day of Week Heatmap ── */}
              {dowData.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Spend by Day of Week
                    </Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={dowData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} width={80} />
                        <RechartsTooltip
                          formatter={v => [formatCurrency(v), 'Spend']}
                          contentStyle={{
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="totalSpent" radius={[4, 4, 0, 0]}>
                          {dowData.map((entry, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* ── Section 5: Detailed Merchant Table ── */}
              <Card variant="outlined">
                <CardContent sx={{ pb: '8px !important' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      All Merchants ({filtered.length})
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <TextField
                      size="small"
                      placeholder="Search merchants…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 220 }}
                    />
                  </Stack>
                </CardContent>

                <TableContainer>
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700, width: 40, pl: 2 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortField === 'merchant'}
                            direction={sortField === 'merchant' ? sortDir : 'asc'}
                            onClick={() => handleSort('merchant')}
                          >
                            Merchant
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortField === 'totalSpent'}
                            direction={sortField === 'totalSpent' ? sortDir : 'desc'}
                            onClick={() => handleSort('totalSpent')}
                          >
                            Total Spent
                          </TableSortLabel>
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={sortField === 'txCount'}
                              direction={sortField === 'txCount' ? sortDir : 'desc'}
                              onClick={() => handleSort('txCount')}
                            >
                              # Tx
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {!isMd && (
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={sortField === 'avgAmount'}
                              direction={sortField === 'avgAmount' ? sortDir : 'desc'}
                              onClick={() => handleSort('avgAmount')}
                            >
                              Avg Spend
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {!isMd && (
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={sortField === 'maxAmount'}
                              direction={sortField === 'maxAmount' ? sortDir : 'desc'}
                              onClick={() => handleSort('maxAmount')}
                            >
                              Max Spend
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={sortField === 'lastSeen'}
                              direction={sortField === 'lastSeen' ? sortDir : 'desc'}
                              onClick={() => handleSort('lastSeen')}
                            >
                              Last Seen
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sorted.map((m, idx) => {
                        const rowId  = m.merchantKey || m.merchant || '';
                        const isOpen = expanded === rowId;
                        const color  = CHART_COLORS[merchants.indexOf(m) % CHART_COLORS.length];
                        const pct    = totalSpend > 0 ? (m.totalSpent / totalSpend) * 100 : 0;

                        return (
                          <React.Fragment key={rowId || idx}>
                            <TableRow
                              hover
                              sx={{
                                cursor: 'pointer',
                                bgcolor: isOpen ? 'action.selected' : undefined,
                              }}
                              onClick={() => rowId && setExpanded(isOpen ? null : rowId)}
                            >
                              {/* Rank */}
                              <TableCell sx={{ pl: 2, color: 'text.secondary', fontWeight: 600 }}>
                                {idx + 1}
                              </TableCell>

                              {/* Merchant name */}
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box
                                    sx={{
                                      width: 32, height: 32, borderRadius: '50%',
                                      bgcolor: `${color}22`, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color, fontWeight: 700, fontSize: 13, flexShrink: 0,
                                    }}
                                  >
                                    {(m.merchant || '?').charAt(0).toUpperCase()}
                                  </Box>
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }}>
                                    {m.merchant || 'Unknown'}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              {/* Total spent with mini progress bar */}
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={700} color="error.main">
                                  {formatCurrency(m.totalSpent)}
                                </Typography>
                                <Box sx={{ height: 3, borderRadius: 2, bgcolor: 'action.hover', mt: 0.4, minWidth: 60 }}>
                                  <Box
                                    sx={{
                                      height: '100%', borderRadius: 2,
                                      bgcolor: 'error.main',
                                      width: `${Math.min(pct, 100)}%`,
                                    }}
                                  />
                                </Box>
                              </TableCell>

                              {!isMobile && (
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">{m.txCount}</Typography>
                                </TableCell>
                              )}
                              {!isMd && (
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {formatCurrency(m.avgAmount || 0)}
                                  </Typography>
                                </TableCell>
                              )}
                              {!isMd && (
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {formatCurrency(m.maxAmount || 0)}
                                  </Typography>
                                </TableCell>
                              )}
                              {!isMobile && (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(m.lastSeen)}
                                  </Typography>
                                </TableCell>
                              )}

                              {/* Top category */}
                              <TableCell>
                                {m.categories && m.categories[0] ? (
                                  <Chip
                                    label={m.categories[0].name}
                                    size="small"
                                    sx={{
                                      bgcolor: m.categories[0].color ? `${m.categories[0].color}22` : 'action.hover',
                                      color: m.categories[0].color || undefined,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </TableCell>

                              {/* Expand toggle */}
                              <TableCell align="center" sx={{ pr: 1 }}>
                                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </TableCell>
                            </TableRow>

                            {/* Expanded transaction drawer */}
                            <TableRow>
                              <TableCell
                                colSpan={isMd ? 5 : (isMobile ? 4 : 8)}
                                sx={{ p: 0, border: isOpen ? undefined : 'none' }}
                              >
                                <Collapse in={isOpen} unmountOnExit>
                                  <Box sx={{ bgcolor: 'action.hover' }}>
                                    <Divider />
                                    <MerchantTransactions
                                      merchantName={m.merchant || ''}
                                      merchantKey={m.merchantKey || ''}
                                      from={from}
                                      to={to}
                                    />
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
}
